import PropTypes from 'prop-types';
import { useCurrency } from '../../shared/hooks/useCurrency.ts';
import {
    isPriceFilter,
    isDimensionFilter,
    isWhiteGloveFilter,
    isFreeShippingFilter,
    isColorFilter,
    isDeliveredByFilter,
    isLocalPickupFilter,
    isRingSizeFilter,
    isNetTradePriceDiscount,
    isTableWidthFilter,
} from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { getPricePillValue } from '../SbSharedAppliedFilters/sbSharedAppliedFiltersValueHelper.tsx';
import { sbSharedRefineMenuMessages } from '../SbSharedRefineMenu/sbSharedRefineMenuMessages.tsx';
import { RING_SIZE_MM } from '../../constants/ringSize.ts';
import { trackPillDeselectClick } from '../../utils/tracking/searchBrowse/filterTracking.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

// components
import { FormattedNumber, FormattedMessage, useIntl } from 'dibs-react-intl';
import { SearchTag } from 'dibs-elements/exports/SearchTag.tsx';

// styles
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbRespAppliedFiltersItem.scss';

const whiteGloveFilterMaxValue = filterValue => filterValue && filterValue.displayName;

const minMaxToAppliedFilterValue = (min, max) =>
    sbSharedRefineMenuMessages.minMaxFormattedMessage({ isMobile: false, min, max });

const whiteGloveFilterValue = ({ currency, filterValue }) => {
    const min = (
        <FormattedNumber value={0} style="currency" currency={currency} minimumFractionDigits={0} />
    );

    const max = (
        <FormattedNumber
            value={whiteGloveFilterMaxValue(filterValue)}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
    );

    return minMaxToAppliedFilterValue(min, max);
};

function getMessageType({ value, filterName }) {
    if (!value) {
        return 'noValue';
    }
    if (isColorFilter(filterName)) {
        return 'color';
    }
    return 'other';
}

export const SbRespAppliedFiltersItem = props => {
    const {
        localizedFilterName,
        filterName,
        filterValue,
        onCloseClick,
        measurementUnit,
        ringMeasurementType,
    } = props;
    const displayName = filterValue?.displayName || null;
    const urlLabel = filterValue?.urlLabel || null;
    let localizedFilterValue = localizedFilterName;
    let value = displayName;
    let dimension = null;
    const intl = useIntl();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const { currency } = useCurrency();

    if (isDeliveredByFilter(filterName)) {
        dimension = (
            <FormattedMessage
                id="sb.SbRespAppliedFiltersItem.dimensionDays"
                defaultMessage=" Days"
            />
        );
    } else if (isLocalPickupFilter(filterName)) {
        value = null;
    } else if (isWhiteGloveFilter(filterName)) {
        value = whiteGloveFilterValue({ currency, filterValue });
    } else if (isPriceFilter(filterName)) {
        value = getPricePillValue({ currency, displayName, isMobile: false });
    } else if (isDimensionFilter(filterName)) {
        value = sbSharedRefineMenuMessages.formatMeasurementFromUrlLabel({
            intl,
            urlLabel,
            measurementUnit,
            shouldConvertValue: false,
        });
    } else if (isFreeShippingFilter(filterName)) {
        localizedFilterValue = sbSharedRefineMenuMessages.shippingOptions;
        value = sbSharedRefineMenuMessages.shippingFree;
    } else if (isRingSizeFilter(filterName)) {
        if (ringMeasurementType === RING_SIZE_MM) {
            value = filterValue?.properties?.mm;
            dimension = (
                <FormattedMessage id="sb.SbRespAppliedFiltersItem.mmRingSize" defaultMessage="mm" />
            );
        }
    } else if (isNetTradePriceDiscount(filterName)) {
        dimension = (
            <FormattedMessage
                id="sb.SbRespAppliedFiltersItem.netPriceDiscount"
                defaultMessage="% or more"
            />
        );
    } else if (isTableWidthFilter(filterName)) {
        localizedFilterValue = sbSharedRefineMenuMessages.width;
        value = sbSharedRefineMenuMessages.formatMeasurementFromUrlLabel({
            intl,
            urlLabel,
            measurementUnit,
            shouldConvertValue: true,
        });
    }

    const handleDelete = event => {
        onCloseClick(event);
        trackPillDeselectClick(filterName, urlLabel);
    };

    return (
        <div className={classnames(dibsCss.mrXsmall, dibsCss.mbSmall)}>
            <SearchTag
                dataTn={`filter-tag-${filterName}`}
                onDelete={handleDelete}
                isActive
                disabled={refetchInFlight}
            >
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.overflowXHidden,
                        dibsCss.whitespacePre,
                        dibsCss.grow,
                        dibsCss.shrink,
                        dibsCss.basis0
                    )}
                >
                    <FormattedMessage
                        id="sb.SbRespAppliedFiltersItem.filter"
                        defaultMessage="{type, select,
                            noValue {{filterName}}
                            color {{filterName}: {colorDot}&nbsp;{filterValue}}
                            other {{filterName}: {filterValue}}
                        }"
                        values={{
                            type: getMessageType({ value, filterName }),
                            filterName: (
                                <span data-tn={`label-${filterName}`}>{localizedFilterValue}</span>
                            ),
                            colorDot: (
                                <span
                                    className={classnames(
                                        styles.colorDot,
                                        dibsCss.inlineBlock,
                                        dibsCss.roundedFull
                                    )}
                                    style={{ backgroundColor: filterValue?.hexCode }}
                                />
                            ),
                            filterValue: (
                                <span
                                    className={classnames(
                                        dibsCss.sassyFontBodySizeNoneTypeHeavy,
                                        dibsCss.overflowXHidden,
                                        dibsCss.textEllipsis
                                    )}
                                    data-tn={`value-${filterName}`}
                                >
                                    {value}
                                    {dimension || ''}
                                </span>
                            ),
                        }}
                    />
                </div>
            </SearchTag>
        </div>
    );
};

SbRespAppliedFiltersItem.propTypes = {
    filterName: PropTypes.string.isRequired,
    onCloseClick: PropTypes.func.isRequired,
    filterValue: PropTypes.object,
    measurementUnit: PropTypes.string,
    ringMeasurementType: PropTypes.string,
    localizedFilterName: PropTypes.string,
};
